import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Collapse, Divider, Form, Input, Modal, Row, Space, Switch, Typography } from 'antd';
import '../../ExpandedMapView/ExpandedMapView.scss';
import React, { useEffect } from 'react';
import { ModalVisibility } from '../../ExpandedMapView';
import MapPortalService from '../../../../services/MapPortal/mapportal.service';
import { useNavigate } from 'react-router-dom';
import { MapSettingsPermissions } from '../../../../types/MapSetting';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AssetManagerService from '../../../../services/AssetManager/assetManager.service';
import { useAuth } from '../../../../context/AuthContext';
import { QueryKeys } from '../../../../enums/queryKeys';
import { Controller, useForm } from 'react-hook-form';
import './PermissionsTab.scss';

const permissions = [
  {
    key: 'PUBLIC',
    value: 'Map is visible to anyone with no login required, and appears in the public map portal.',
  },
  {
    key: 'HIDDEN',
    value: 'Map can be accessed through the hidden URL with no sign-in required, and will not appear in the public map portal.',
  },
  {
    key: 'PASSWORD',
    value: 'Map can be accessed by entering a password, and is also available to signed-in users.',
  },
  {
    key: 'GROUPS',
    value: 'Map can only be accessed by signed-in users in the selected groups.',
  },
];

//@ts-ignore
interface PermissionsTabProps {
  isDeleteModalVisible: boolean;
  isPasswordModalVisible: boolean;
  isGroupModalVisible: boolean;
  showModal: (modalType: keyof ModalVisibility) => void;
  handleCancel: (modalType: keyof ModalVisibility) => void;
  mapId: string;
  settings?: MapSettingsPermissions;
  updateSettings: (key: any, value: any) => void;
}

const PermissionsTab: React.FC<PermissionsTabProps> = ({
  settings,
  updateSettings,
  isDeleteModalVisible,
  isPasswordModalVisible,
  isGroupModalVisible,
  showModal,
  handleCancel,
  mapId,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { deleteMap } = MapPortalService();
  const { mutateAsync: deleteMapAsync } = useMutation(deleteMap());
  const { selectedClient } = useAuth();
  const { getStatus } = AssetManagerService();
  const { data: assetManagerStatus } = useQuery(getStatus(selectedClient?.id));
  const isAssetManagerMap = assetManagerStatus?.assetMapId != null && String(assetManagerStatus.assetMapId) === mapId;

  const handleDeleteMap = async () => {
    handleCancel('delete');
    try {
      await deleteMapAsync({ mapId });
    } catch {
      // deleteMap already notifies on failure; stay on the map.
      return;
    }
    queryClient.removeQueries({ queryKey: [QueryKeys.GET_MAP_BY_ID, mapId] });
    // The 30s global staleTime would otherwise keep the deleted map in the listing.
    queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ALL_MAPS] });
    navigate(-1);
  };

  const passwordForm = useForm<{ password: string; confirmPassword: string }>({
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (isPasswordModalVisible) {
      passwordForm.reset({ password: '', confirmPassword: '' });
    }
  }, [isPasswordModalVisible, passwordForm.reset]);

  const submitPassword = passwordForm.handleSubmit(({ password, confirmPassword }) => {
    if (password !== confirmPassword) return;
    updateSettings('password', confirmPassword);
    handleCancel('password');
  });

  return (
    <>
      <div className="permissions-content">
        <Row className="map-status">
          <Col>
            <span>Map Status</span>
          </Col>
          <Col className="status-toggle">
            <span>OFFLINE</span>
            <Switch value={settings?.isLive} onChange={() => updateSettings('isLive', !settings?.isLive)} />
            <span>LIVE</span>
          </Col>
          {!isAssetManagerMap && (
            <Col>
              <Button
                type="primary"
                danger
                onClick={() => {
                  showModal('delete');
                }}
              >
                DELETE MAP
              </Button>
            </Col>
          )}
        </Row>

        <Modal
          centered
          open={isDeleteModalVisible}
          onCancel={() => handleCancel('delete')}
          footer={null}
          closable={true}
          closeIcon={<span className="permissions-tab__close">&times;</span>}
          className="permissions-tab__modal"
        >
          <div className="permissions-tab__center">
            <Space direction="horizontal">
              <ExclamationCircleOutlined className="permissions-tab__warning-icon" />
              <Typography.Title level={4}>WARNING!</Typography.Title>
            </Space>
            <p>THE CURRENT MAP WILL BE PERMANENTLY DELETED. THIS CANNOT BE UNDONE.</p>
            <p>ARE YOU SURE YOU WISH TO DELETE THIS MAP?</p>
            <div className="permissions-tab__actions">
              <Button onClick={() => handleCancel('delete')} className="permissions-tab__btn-mr">
                NO, GO BACK
              </Button>
              <Button type="primary" danger onClick={handleDeleteMap}>
                YES, DELETE
              </Button>
            </div>
          </div>
        </Modal>

        <div className={!settings?.isLive ? 'permissions-tab__hidden' : ''}>
          <Typography.Title level={4}>Permissions</Typography.Title>
          <Divider className="permissions-divider" />
          <div className="permission-options">
            {permissions.map((option) => {
              switch (option.key) {
                case 'PASSWORD':
                  return (
                    <Space className="option" key={option.key}>
                      <Col>
                        <Button onClick={() => showModal('password')}>{option.key}</Button>
                      </Col>
                      <Col className="permission-value">
                        <span>{option.value}</span>
                      </Col>
                    </Space>
                  );
                case 'GROUPS':
                  return (
                    <React.Fragment key={option.key}>
                      <Space className="option" key={option.key}>
                        <Col>
                          <Button onClick={() => showModal('group')}>{option.key}</Button>
                        </Col>
                        <Col className="permission-value">
                          <span>{option.value}</span>
                        </Col>
                      </Space>
                      <Modal
                        title="GROUPS"
                        open={isGroupModalVisible}
                        onCancel={() => handleCancel('group')}
                        footer={<Button className="permissions-tab__modal-footer-btn">Save Groups</Button>}
                        centered
                      >
                        <p>Only the selected user groups will be able to access and view the map.</p>

                        <Collapse defaultActiveKey={['1']} className="permissions-tab__collapse">
                          <Collapse.Panel header="USERS" key="1">
                            <Checkbox.Group className="permissions-tab__checkbox-group">
                              <Row>
                                {settings?.users.map((user: any) => (
                                  <Col span={24} key={user.name}>
                                    <Checkbox value={user?.name} className="permissions-tab__checkbox">
                                      {user.name}
                                    </Checkbox>
                                  </Col>
                                ))}
                              </Row>
                            </Checkbox.Group>
                          </Collapse.Panel>
                          <Collapse.Panel header="GROUPS" key="2">
                            <Checkbox.Group className="permissions-tab__checkbox-group">
                              <Row>
                                {settings?.groups.map((group: any) => (
                                  <Col span={24} key={group?.name}>
                                    <Checkbox value={group?.name}>{group?.name}</Checkbox>
                                  </Col>
                                ))}
                              </Row>
                            </Checkbox.Group>
                          </Collapse.Panel>
                        </Collapse>
                        <Button className="permissions-tab__mt10">+ Add New Viewer</Button>
                      </Modal>
                    </React.Fragment>
                  );

                default:
                  return (
                    <Space className="option" key={option.key}>
                      <Col>
                        <Button>{option.key}</Button>
                      </Col>
                      <Col className="permission-value">
                        <span>{option.value}</span>
                      </Col>
                    </Space>
                  );
              }
            })}
          </div>
        </div>
      </div>

      <Modal
        title="PASSWORD PROTECTED MAP"
        centered
        footer={null}
        closable={true}
        open={isPasswordModalVisible}
        onCancel={() => handleCancel('password')}
        closeIcon={<span className="permissions-tab__close">&times;</span>}
        className="permissions-tab__modal"
      >
        <Divider />
        <div>
          <p>Password-protected maps can only be accessed by entering the password upon navigating to the web URL.</p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submitPassword();
            }}
          >
            <Form.Item className="form-item">
              <Controller
                name="password"
                control={passwordForm.control}
                rules={{ required: 'Password is required.' }}
                render={({ field }) => (
                  <Input.Password
                    {...field}
                    id="mapPassword"
                    className="custom-input permissions-tab__password-input"
                    placeholder="Enter map password..."
                    status={passwordForm.formState.errors.password ? 'error' : undefined}
                  />
                )}
              />
            </Form.Item>
            <Form.Item className="form-item">
              <Controller
                name="confirmPassword"
                control={passwordForm.control}
                rules={{
                  required: 'Please confirm your password.',
                  validate: (val) => val === passwordForm.getValues().password || 'Passwords do not match!',
                }}
                render={({ field }) => (
                  <Input.Password
                    {...field}
                    id="confirmMapPassword"
                    className="custom-input permissions-tab__password-input"
                    placeholder="Confirm map password..."
                    status={passwordForm.formState.errors.confirmPassword ? 'error' : undefined}
                  />
                )}
              />
            </Form.Item>
            <Row justify="center">
              <Button className="permissions-tab__password-btn" htmlType="submit">
                Set Password
              </Button>
            </Row>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default PermissionsTab;
